import { useTranslate } from "@tolgee/react";
import { debounce } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  type FieldValues,
  type Path,
  type UseFormReturn,
} from "react-hook-form";
import { useOrders } from "@/hooks";

interface UseDebouncedOrderNameCheckProps<T extends FieldValues> {
  methods: UseFormReturn<T>;
  fieldName: Path<T>;
  orderIdToExclude?: string;
}

export const useOrderNameCheck = <T extends FieldValues>(
  props: UseDebouncedOrderNameCheckProps<T>
) => {
  // Hooks
  const { t } = useTranslate();

  // Query hooks
  const { useOrderNameAvailability } = useOrders();
  const { mutateAsync: checkOrderNameAvailability } =
    useOrderNameAvailability();

  // States
  const [isCheckPending, setIsCheckPending] = useState(false);
  const [isAvailable, setIsAvailable] = useState(true);

  // Refs to avoid stale closures
  const methodsRef = useRef(props.methods);

  // Debounced check
  const debouncedCheck = useCallback(
    debounce(async (value: string) => {
      if (!value) {
        return;
      }

      const isValid = await methodsRef.current.trigger(props.fieldName);
      if (!isValid) {
        return;
      }

      try {
        const res = await checkOrderNameAvailability({
          orderName: value,
          orderIdToExclude: props.orderIdToExclude,
        });

        if (!res.data.available) {
          methodsRef.current.setError(props.fieldName, {
            type: "custom",
            message: t(
              "order.mediumDetails.stepConfiguration.duplicateOrderNameError"
            ),
          });
          setIsAvailable(false);
        } else {
          methodsRef.current.clearErrors(props.fieldName);
          setIsAvailable(true);
        }
      } finally {
        setIsCheckPending(false);
      }
    }, 500),
    []
  );

  // Subscribe to changes
  useEffect(() => {
    const subscription = props.methods.watch((value, { name }) => {
      if (name === props.fieldName) {
        setIsCheckPending(true);

        const newValue = (value?.[props.fieldName] as string) ?? value;
        void debouncedCheck(newValue);
      }
    });

    return () => subscription.unsubscribe();
  }, [debouncedCheck, props.fieldName, props.methods]);

  // Cancel debounce on unmount
  useEffect(() => {
    return () => {
      debouncedCheck.cancel();
    };
  }, [debouncedCheck]);

  return {
    isCheckPending,
    isAvailable,
  };
};
